import * as React from "react";
import { useState } from "react";
import {
  Avatar,
  Text,
  Caption1,
  Body1,
  Badge,
  Button,
  Tooltip,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import { Table24Regular, Document24Regular, Edit20Regular, Delete20Regular } from "@fluentui/react-icons";
import { DiscussionDto } from "../../../services/discussions.service";
import { StatusItemDto } from "../../../services/status-items.service";
import { ExcelService } from "../../../services/excel.service";
import { isStandaloneMode } from "../../../utils/standalone-mode";
import { findTypeById } from "../types/discussionTypes";
import RichTextDisplay from "./RichTextDisplay";
import { normalizeStatus, getStatusColor } from "../../../utils/discussionStatusHelpers";
import { useTypographyStyles } from "../../../styles/typography.styles";

const useStyles = makeStyles({
  container: {
    display: "flex",
    gap: tokens.spacingHorizontalM,
    padding: tokens.spacingVerticalM,
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
    marginBottom: tokens.spacingVerticalM,
  },
  avatar: {
    flexShrink: 0,
  },
  content: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    flexWrap: "wrap",
  },
  timestamp: {
    color: tokens.colorNeutralForeground3,
  },
  badgeGroup: {
    display: "flex",
    gap: tokens.spacingHorizontalXS,
    marginLeft: "auto",
  },
  description: {
    color: tokens.colorNeutralForeground1,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  linkedItemsContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: tokens.spacingHorizontalXS,
    marginTop: tokens.spacingVerticalS,
  },
  linkedItemBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalXXS,
    padding: `${tokens.spacingVerticalXXS} ${tokens.spacingHorizontalS}`,
    borderRadius: tokens.borderRadiusMedium,
    fontSize: tokens.fontSizeBase200,
  },
  linkedItemClickable: {
    cursor: "pointer",
    backgroundColor: tokens.colorPaletteGreenBackground2,
    color: tokens.colorPaletteGreenForeground2,
    border: `1px solid ${tokens.colorPaletteGreenBorder2}`,
    ":hover": {
      backgroundColor: tokens.colorPaletteGreenBackground1,
      textDecoration: "underline",
    },
  },
  linkedItemStatic: {
    backgroundColor: tokens.colorPaletteBlueBackground2,
    color: tokens.colorPaletteBlueForeground2,
    border: `1px solid ${tokens.colorPaletteBlueBorderActive}`,
  },
  archivedText: {
    color: tokens.colorNeutralForeground4,
    fontStyle: "italic",
  },
});

interface DiscussionPostProps {
  discussion: DiscussionDto;
  statusItems?: StatusItemDto[];
  isEditable?: boolean;
  isArchivable?: boolean;
  onEditClick?: () => void;
  onArchiveClick?: () => void;
}

// Helper to format date
const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) {
    return "just now";
  } else if (diffMins < 60) {
    return diffMins === 1 ? "1 minute ago" : `${diffMins} minutes ago`;
  } else if (diffHours < 24) {
    return diffHours === 1 ? "1 hour ago" : `${diffHours} hours ago`;
  } else if (diffDays < 7) {
    return diffDays === 1 ? "1 day ago" : `${diffDays} days ago`;
  } else {
    return date.toLocaleDateString();
  }
};

// Helper to get initials from name
const getInitials = (name: string): string => {
  const parts = name.trim().split(" ");
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

// Check if a discussion has been edited (updatedAt differs from createdAt by more than 1 second)
const isEdited = (discussion: DiscussionDto): boolean => {
  const created = new Date(discussion.createdAt).getTime();
  const updated = new Date(discussion.updatedAt).getTime();
  return updated - created > 1000;
};

/**
 * Renders the original discussion post at the top of the thread —
 * author, timestamp, type pill, description (rich text), and tags.
 */
const DiscussionPost: React.FC<DiscussionPostProps> = ({
  discussion,
  statusItems,
  isEditable,
  isArchivable,
  onEditClick,
  onArchiveClick,
}) => {
  const styles = useStyles();
  const typography = useTypographyStyles();
  const [isArchiveDialogOpen, setIsArchiveDialogOpen] = useState(false);

  const mainTypeDetails = findTypeById(discussion.type);
  const mainTypeColor = mainTypeDetails?.color || "#0078D4";

  // Resolve status item IDs to status item objects
  const linkedItems = (discussion.statusItemIds || []).map((id) => {
    const item = statusItems?.find((si) => si.id === id);
    return item || null;
  });

  const handleItemClick = async (item: StatusItemDto) => {
    if (!item.sheetId) return;
    // Jump-to-sheet is Excel-only. In standalone/dialog we leave the chip
    // clickable but it no-ops — the linked status item is still visible in
    // the list, users just can't teleport to it.
    if (isStandaloneMode()) return;
    try {
      await ExcelService.selectCellById(item.sheetId, "A1");
    } catch (error) {
      console.error("Failed to navigate to sheet:", error);
    }
  };

  return (
    <div className={styles.container}>
      <Avatar
        className={styles.avatar}
        name={discussion.createdByName}
        size={48}
        initials={getInitials(discussion.createdByName)}
        color="colorful"
      />
      <div className={styles.content}>
        <div className={styles.header}>
          <Text className={typography.sectionTitle}>
            {discussion.createdByName}
          </Text>
          <Caption1 className={styles.timestamp}>
            {formatDate(discussion.createdAt)}
            {!discussion.isArchived && isEdited(discussion) && " (edited)"}
          </Caption1>
          {isEditable && (
            <Tooltip content="Edit discussion" relationship="label">
              <Button
                appearance="subtle"
                size="small"
                icon={<Edit20Regular />}
                onClick={onEditClick}
              />
            </Tooltip>
          )}
          {isArchivable && (
            <Tooltip content="Archive discussion" relationship="label">
              <Button
                appearance="subtle"
                size="small"
                icon={<Delete20Regular />}
                onClick={() => setIsArchiveDialogOpen(true)}
              />
            </Tooltip>
          )}
          <div className={styles.badgeGroup}>
            {discussion.status && (
              <Badge
                appearance="filled"
                style={{
                  backgroundColor: getStatusColor(discussion.status),
                  color: "white",
                }}
              >
                {normalizeStatus(discussion.status)}
              </Badge>
            )}
            {discussion.type && (
              <Badge
                appearance="filled"
                style={{
                  backgroundColor: mainTypeColor,
                  color: "white",
                }}
              >
                {mainTypeDetails?.name || discussion.type}
              </Badge>
            )}
            {discussion.tags && discussion.tags.length > 0 && (
              discussion.tags.map((tagId) => {
                const tagDetails = findTypeById(tagId);
                return tagDetails ? (
                  <Badge
                    key={tagId}
                    appearance="filled"
                    style={{
                      backgroundColor: tagDetails.color,
                      color: "white",
                    }}
                  >
                    {tagDetails.name}
                  </Badge>
                ) : null;
              })
            )}
          </div>
        </div>
        {discussion.isArchived ? (
          <Body1 className={styles.archivedText}>
            Discussion archived
          </Body1>
        ) : discussion.descriptionContent ? (
          <Body1 className={styles.description}>
            <RichTextDisplay markup={discussion.descriptionContent} />
          </Body1>
        ) : discussion.description ? (
          <Body1 className={styles.description}>{discussion.description}</Body1>
        ) : null}

        {/* Linked status items for review_point discussions */}
        {discussion.type === "review_point" && linkedItems.length > 0 && (
          <div className={styles.linkedItemsContainer}>
            {linkedItems.map((item, index) => {
              const itemId = discussion.statusItemIds[index];
              if (!item) {
                return (
                  <span key={itemId} className={styles.linkedItemBadge} style={{ opacity: 0.5 }}>
                    <Table24Regular style={{ fontSize: "14px" }} />
                    [Deleted item]
                  </span>
                );
              }
              if (item.sheetId) {
                return (
                  <span
                    key={item.id}
                    className={`${styles.linkedItemBadge} ${styles.linkedItemClickable}`}
                    onClick={() => handleItemClick(item)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleItemClick(item);
                      }
                    }}
                  >
                    <Table24Regular style={{ fontSize: "14px" }} />
                    {item.name}
                  </span>
                );
              }
              return (
                <span
                  key={item.id}
                  className={`${styles.linkedItemBadge} ${styles.linkedItemStatic}`}
                >
                  <Document24Regular style={{ fontSize: "14px" }} />
                  {item.name}
                </span>
              );
            })}
          </div>
        )}
      </div>

      <Dialog
        open={isArchiveDialogOpen}
        onOpenChange={(_e, data) => setIsArchiveDialogOpen(data.open)}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Archive discussion</DialogTitle>
            <DialogContent>
              Are you sure you want to archive this discussion? The content will no longer be visible.
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setIsArchiveDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                appearance="primary"
                onClick={() => {
                  setIsArchiveDialogOpen(false);
                  onArchiveClick?.();
                }}
              >
                Archive
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};

export default DiscussionPost;
